import { Flex, IconButton, Text, VStack } from "@chakra-ui/react";
import { useField } from "formik";
import { NumberInputControl, SelectControl } from "formik-chakra-ui";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { isNotNull } from "src/services/predicates";
import { type CapacityOperator, type CapacityUnit } from "./capacityCondition";

export function CapacityConditionCard({
  name,
  onDelete,
}: {
  name: string;
  onDelete: () => void;
}) {
  const [operatorField] = useField<CapacityOperator | "">(`${name}.operator`);
  const [unitField] = useField<CapacityUnit | "">(`${name}.unit`);
  const operator = operatorField.value;
  const numberInputProps = {
    min: 0,
    ...(unitField.value === "percent" ? { max: 100 } : {}),
  };

  return (
    <Card showBorder>
      <VStack align="stretch" spacing={4}>
        <Flex align="center" justify="space-between" gap={3}>
          <Text fontSize="md" fontWeight="medium" color="blackAlpha.800">
            Capacity condition
          </Text>
          <IconButton
            aria-label="Delete capacity"
            icon={<RiDeleteBin6Line />}
            variant="outline"
            colorScheme="gray"
            size="sm"
            onClick={onDelete}
          />
        </Flex>
        <SelectControl
          name={`${name}.operator`}
          label="Capacity available is"
          selectProps={{ placeholder: "Select" }}
        >
          <option value="lessThan">Less than</option>
          <option value="greaterThan">Greater than</option>
          <option value="between">Between</option>
        </SelectControl>

        {operator === "lessThan" || operator === "greaterThan" ? (
          <Flex align="stretch" gap={2}>
            <NumberInputControl
              name={`${name}.value`}
              label="Quantity"
              flexGrow={1}
              showStepper={false}
              numberInputProps={numberInputProps}
            />
            <UnitSelect name={name} />
          </Flex>
        ) : null}

        {operator === "between" ? (
          <Flex align="stretch" gap={2}>
            <NumberInputControl
              name={`${name}.value`}
              label="Minimum"
              flexGrow={1}
              showStepper={false}
              numberInputProps={numberInputProps}
            />
            <NumberInputControl
              name={`${name}.endValue`}
              label="Maximum"
              flexGrow={1}
              showStepper={false}
              numberInputProps={numberInputProps}
            />
            <UnitSelect name={name} />
          </Flex>
        ) : null}
      </VStack>
    </Card>
  );
}

function UnitSelect({ name }: { name: string }) {
  const fieldName = `${name}.unit`;
  const [, meta] = useField<string>(fieldName);
  const showError = meta.touched && isNotNull(meta.error);

  return (
    <SelectControl
      name={fieldName}
      label="Unit"
      labelProps={{ visibility: "hidden", "aria-hidden": true }}
      isInvalid={showError}
      flexShrink={0}
      width="8rem"
      selectProps={{ placeholder: "Select", "aria-label": "Unit" }}
    >
      <option value="seats">seats</option>
      <option value="percent">%</option>
    </SelectControl>
  );
}
